<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'

import { vClickOutside } from '~/directives/ClickOutside'

import NotificationsMenu from '~/components/notifications-v2/NotificationsMenu.vue'
import BtnIcon from '~/components/ui/Buttons/icon.vue'
import VText from '~/components/ui/VText.vue'

import { useNotificationSocket } from '~/composables/useNotificationSocket'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'

import { provideNotificationGetUnpeekedCount } from '~/api-core/Notifications/GetUnpeekedCount'
import { provideNotificationPeek } from '~/api-core/Notifications/MarkAsPeeked'

import { Breakpoints } from '~/enums/breakpoints'

type Emits = {
  'menu-open': []
}

const emit = defineEmits<Emits>()
const uniqueId = useId()
const { addSocketEvent, removeSocketEvent } = useNotificationSocket()
const { coreFetch } = useProvideCoreFetch()

const unreadItems = ref(0)
const socketListenerId = ref<string | null>(null)
const displayNotificationsMenu = ref(false)

const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { id: USER_BAND_ID, HAS_BAND_SELECTED: USER_HAS_BAND_SELECTED } =
  storeToRefs(useUserBandStore())
const { IS_INF: USER_IS_INF, IS_BAND: USER_IS_BAND } =
  storeToRefs(useUserStore())

const unreadItemsDisplayValue = computed(() =>
  unreadItems.value < 10 ? unreadItems.value.toString() : '9+',
)

const displaySelf = computed(
  () =>
    USER_IS_INF.value || (USER_IS_BAND.value && USER_HAS_BAND_SELECTED.value),
)

watch(displaySelf, () => {
  refreshUnreadItemsCount()
})
onMounted(async () => {
  socketListenerId.value = addSocketEvent(() => {
    if (displayNotificationsMenu.value) return

    unreadItems.value += 1
  })
  await refreshUnreadItemsCount()
})

onBeforeUnmount(() => {
  if (!socketListenerId.value) return
  removeSocketEvent(socketListenerId.value)
})

async function refreshUnreadItemsCount() {
  if (!displaySelf.value) return

  const getUnreadCount = provideNotificationGetUnpeekedCount(coreFetch)
  const { count } = await (USER_IS_BAND.value
    ? getUnreadCount<'band'>(USER_BAND_ID.value)
    : getUnreadCount<'influencer'>())
  unreadItems.value = count
}

function toggleNotificationsMenu() {
  const wantToOpen = !displayNotificationsMenu.value

  if (wantToOpen) {
    const peekNotifications = provideNotificationPeek(coreFetch)

    ;(USER_IS_BAND.value
      ? peekNotifications<'band'>(USER_BAND_ID.value)
      : peekNotifications<'influencer'>()
    ).then(() => {
      unreadItems.value = 0
    })
    emit('menu-open')
  }
  displayNotificationsMenu.value = !displayNotificationsMenu.value
}

function closeNotificationsMenu() {
  if (!displayNotificationsMenu.value) return

  displayNotificationsMenu.value = false
}

defineExpose({
  closeNotificationsMenu,
})
</script>

<template>
  <div
    v-if="displaySelf"
    v-click-outside="closeNotificationsMenu"
    class="notificationMenuItem tw-flex tw-items-center"
  >
    <div class="tw-relative tw-cursor-pointer" @click="toggleNotificationsMenu">
      <BtnIcon
        data-test-id="bandTopNavigationNotificationsCTA"
        :aria-controls="displayNotificationsMenu ? uniqueId : undefined"
        :aria-expanded="displayNotificationsMenu"
        :aria-label="$t('notifications.toggleMenu')"
      >
        <UIIcon
          :name="
            displayNotificationsMenu ? 'fa6-solid:bell' : 'fa6-regular:bell'
          "
          class="tw-text-inverse"
          :class="{ displayNotificationsMenu }"
        />
      </BtnIcon>
      <transition name="fade" mode="out-in">
        <div
          v-if="unreadItems"
          class="tw-absolute tw-bottom-5 tw-left-3 tw-overflow-hidden tw-rounded-full tw-border-2 tw-border-default tw-bg-orange-500"
          aria-hidden
        >
          <VText
            cfg="sans/13/medium"
            color="white"
            class="tw-flex tw-h-4 tw-min-w-[16px] tw-items-center tw-justify-center tw-rounded-full tw-leading-3"
            :class="{ '!tw-min-w-[24px]': unreadItems > 9 }"
          >
            {{ unreadItemsDisplayValue }}
          </VText>
        </div>
      </transition>
    </div>
    <NotificationsMenu
      v-if="displayNotificationsMenu"
      :id="uniqueId"
      :mobile="SCREEN_WIDTH < Breakpoints.sm"
      class="notificationsMenu"
      @close="closeNotificationsMenu"
    />
  </div>
</template>

<style lang="scss" scoped>
/**
 * 0 - 1005 - 015
 * 0 - 0100 - ~15
 */
@keyframes bell-wiggle {
  0% {
    transform: rotate(0deg);
  }
  15% {
    transform: rotate(45deg);
  }
  30% {
    transform: rotate(-30deg);
  }
  44% {
    transform: rotate(30deg);
  }
  64% {
    transform: rotate(-15deg);
  }
  84% {
    transform: rotate(15deg);
  }
  100% {
    transform: rotate(0);
  }
}

@media (prefers-reduced-motion: no-preference) {
  .displayNotificationsMenu {
    animation: bell-wiggle 1005ms 1;
    transform-origin: top;
  }
}
</style>
